import { assertSettingsLayoutWrite } from '@/lib/settings/settings-session-access';
import { createClient } from './client';

// ─── Security Helpers ───────────────────────────────────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function sanitizeUuid(value: string): string {
  if (!UUID_RE.test(value)) return '00000000-0000-0000-0000-000000000000';
  return value;
}

// ─── Types ──────────────────────────────────────────────────
export type PrefType = 'sections' | 'columns' | 'stats' | 'user_pref';

export interface LayoutPreference {
  id: string;
  tenant_id: string;
  user_id: string | null;
  page_key: string;
  pref_type: PrefType;
  visible_keys: Record<string, boolean>;
  key_order: string[];
}

export interface PageLayoutRoleDefault {
  id: string;
  tenant_id: string;
  scope_role: string;
  page_key: string;
  visible_keys: Record<string, boolean>;
  key_order: string[];
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Queries ────────────────────────────────────────────────

/**
 * Fetch layout preference for a page.
 * Returns user override if it exists, else org default, else null.
 */
export async function getLayoutPreference(
  tenantId: string,
  userId: string,
  pageKey: string,
  prefType: PrefType,
): Promise<LayoutPreference | null> {
  const sb = createClient();

  // Fetch both org default and user override in one query
  const { data } = await sb
    .from('layout_preferences')
    .select('id, tenant_id, user_id, page_key, pref_type, visible_keys, key_order')
    .eq('tenant_id', tenantId)
    .eq('page_key', pageKey)
    .eq('pref_type', prefType)
    .or(`user_id.eq.${sanitizeUuid(userId)},user_id.is.null`)
    .order('user_id', { ascending: false, nullsFirst: false });

  if (!data || data.length === 0) return null;

  // User override wins over org default (non-null user_id first)
  const userPref = data.find((d) => d.user_id === userId);
  const orgDefault = data.find((d) => d.user_id === null);
  return (userPref ?? orgDefault) as LayoutPreference;
}

/**
 * Fetch only the caller's personal layout override. Unlike getLayoutPreference,
 * this does not fall back to an organisation default row.
 */
export async function getUserLayoutPreference(
  tenantId: string,
  userId: string,
  pageKey: string,
  prefType: PrefType,
): Promise<LayoutPreference | null> {
  const sb = createClient();

  const { data, error } = await sb
    .from('layout_preferences')
    .select('id, tenant_id, user_id, page_key, pref_type, visible_keys, key_order')
    .eq('tenant_id', tenantId)
    .eq('user_id', userId)
    .eq('page_key', pageKey)
    .eq('pref_type', prefType)
    .maybeSingle();

  if (error) throw error;
  return (data as LayoutPreference | null) ?? null;
}

export async function getPageLayoutRoleDefault(
  tenantId: string,
  scopeRole: string,
  pageKey: string,
): Promise<PageLayoutRoleDefault | null> {
  const sb = createClient();

  const { data, error } = await sb
    .from('page_layout_role_defaults')
    .select(
      'id, tenant_id, scope_role, page_key, visible_keys, key_order, updated_by, created_at, updated_at',
    )
    .eq('tenant_id', tenantId)
    .eq('scope_role', scopeRole)
    .eq('page_key', pageKey)
    .maybeSingle();

  if (error) throw error;
  return (data as PageLayoutRoleDefault | null) ?? null;
}

export async function upsertPageLayoutRoleDefault(
  scopeRole: string,
  pageKey: string,
  visibleKeys: Record<string, boolean>,
  keyOrder: string[],
): Promise<PageLayoutRoleDefault | null> {
  const sb = createClient();

  const { data, error } = await sb.rpc('upsert_page_layout_role_default_controlled', {
    p_scope_role: scopeRole,
    p_page_key: pageKey,
    p_visible_keys: visibleKeys,
    p_key_order: keyOrder,
  });

  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return (row as PageLayoutRoleDefault | null) ?? null;
}

/**
 * Check whether the user has a personal override (vs using org default).
 */
export async function hasUserOverride(
  tenantId: string,
  userId: string,
  pageKey: string,
  prefType: PrefType,
): Promise<boolean> {
  const sb = createClient();
  const { count } = await sb
    .from('layout_preferences')
    .select('id', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)
    .eq('user_id', userId)
    .eq('page_key', pageKey)
    .eq('pref_type', prefType);

  return (count ?? 0) > 0;
}

/**
 * Save a user layout preference (pins, dismissed hints, widget position, etc.).
 *
 * NOTE: do NOT use `.upsert({ onConflict: 'tenant_id,user_id,page_key,pref_type' })`
 * here. The only unique index covering those columns is PARTIAL
 * (`uq_layout_preferences_user … WHERE user_id IS NOT NULL`); Postgres cannot use a
 * partial index as an `ON CONFLICT` arbiter from a bare column list, so the upsert
 * raised `42P10` on every call — which the caller (`useUserPreferences`) silently
 * swallowed, meaning user preferences never persisted at all. We mirror the proven
 * insert→catch(23505)→update pattern from `dashboard-preferences.ts` instead.
 *
 * The composite natural key is (tenant_id, user_id, page_key, pref_type). This
 * function always writes a user row (user_id non-null), so the partial unique index
 * raises 23505 on a duplicate insert, which we resolve with an update (last write wins).
 */
export async function saveUserLayoutPreference(
  tenantId: string,
  userId: string,
  pageKey: string,
  prefType: PrefType,
  visibleKeys: Record<string, boolean>,
  keyOrder: string[],
): Promise<void> {
  const sb = createClient();
  await assertSettingsLayoutWrite(sb, tenantId, pageKey);

  const { error: insertError } = await sb.from('layout_preferences').insert({
    tenant_id: tenantId,
    user_id: userId,
    page_key: pageKey,
    pref_type: prefType,
    visible_keys: visibleKeys,
    key_order: keyOrder,
  });

  if (!insertError) return;

  // 23505 = unique_violation → the row already exists; update it by natural key.
  if (insertError.code === '23505') {
    const { error: updateError } = await sb
      .from('layout_preferences')
      // updated_at is also maintained by the layout_prefs_updated_at trigger; we
      // set it explicitly per the "every UPDATE sets updated_at" convention.
      .update({
        visible_keys: visibleKeys,
        key_order: keyOrder,
        updated_at: new Date().toISOString(),
      })
      .eq('tenant_id', tenantId)
      .eq('user_id', userId)
      .eq('page_key', pageKey)
      .eq('pref_type', prefType);
    if (updateError) throw updateError;
    return;
  }

  throw insertError;
}

/**
 * Delete user override so they fall back to org default.
 */
export async function resetUserPreference(
  tenantId: string,
  userId: string,
  pageKey: string,
  prefType: PrefType,
): Promise<void> {
  const sb = createClient();
  await assertSettingsLayoutWrite(sb, tenantId, pageKey);
  const { error } = await sb
    .from('layout_preferences')
    .delete()
    .eq('tenant_id', tenantId)
    .eq('user_id', userId)
    .eq('page_key', pageKey)
    .eq('pref_type', prefType);
  if (error) throw error;
}
